/**
 * TelehealthEncountersList Component
 *
 * Lists all telehealth encounters with search/filter capabilities
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  FileText,
  Search,
  Calendar,
  User,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { telehealthEncounterAPI } from '@/services/api/telehealth';
import { TelehealthEncounter } from './types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface TelehealthEncountersListProps {
  onBack?: () => void;
}

export default function TelehealthEncountersList({ onBack }: TelehealthEncountersListProps) {
  const { fmt } = useFormattedDate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });
  const [detailId, setDetailId] = useState<number | null>(null);

  // Full encounter detail for the drill-in modal.
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['telehealth-encounter-detail', detailId],
    queryFn: () => telehealthEncounterAPI.getEncounter(detailId as number),
    enabled: !!detailId,
  });

  // Fetch encounters
  const {
    data: encountersResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['telehealth-encounters', statusFilter, dateRange],
    queryFn: () =>
      telehealthEncounterAPI.listEncounters({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        start_date: dateRange.start || undefined,
        end_date: dateRange.end || undefined,
      }),
  });

  // Extract encounters array from response (handle both array and object responses)
  const encounters = Array.isArray(encountersResponse)
    ? encountersResponse
    : encountersResponse?.encounters || [];

  // Filter encounters by search query
  const filteredEncounters = encounters.filter((enc: TelehealthEncounter) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      enc.member_name?.toLowerCase().includes(query) ||
      enc.subjective_notes?.toLowerCase().includes(query) ||
      enc.assessment_notes?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      draft: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', label: 'Draft' },
      in_progress: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Completed' },
      signed: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'Signed' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <p className="text-[13px] text-ink-500">
                Clinical &amp; social determinants documentation from telehealth sessions
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search encounters..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="signed">Signed</option>
              </select>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Encounters List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="h-8 w-8 text-teal-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Loading encounters...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Failed to load encounters</p>
            </div>
          ) : !filteredEncounters || filteredEncounters.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No encounters found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Encounters are created when telehealth sessions are started
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredEncounters.map((encounter: TelehealthEncounter) => (
                <div
                  key={encounter.id}
                  onClick={() => setDetailId(encounter.id)}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                        <User className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">
                            {encounter.member_name || `Member #${encounter.member_id}`}
                          </h3>
                          {getStatusBadge(encounter.status)}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {fmt.custom(encounter.encounter_date, 'MMM d, yyyy')}
                          </span>
                          {encounter.sdoh_domains_addressed && encounter.sdoh_domains_addressed.length > 0 && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="h-3.5 w-3.5" />
                              {encounter.sdoh_domains_addressed.length} SDOH domains
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                  {encounter.subjective_notes && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2 ml-14">
                      {encounter.subjective_notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Encounter detail drill-in */}
      <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detail?.member_name || (detail ? `Member #${detail.member_id}` : 'Encounter')}
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-8 text-center text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading encounter…
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                {getStatusBadge(detail.status)}
                <span className="text-gray-500">
                  {(detail.encounter_type || 'telehealth_visit').replace(/_/g, ' ')}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{fmt.custom(detail.encounter_date, 'MMM d, yyyy')}</span>
                {detail.signed_at && (
                  <span className="text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" /> Signed {fmt.custom(detail.signed_at, 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              {[
                ['Chief complaint', detail.chief_complaint],
                ['Reason for visit', detail.reason_for_visit],
                ['Subjective', detail.subjective_notes],
                ['Objective', detail.objective_notes],
                ['Assessment', detail.assessment_notes],
                ['Plan', detail.plan_notes],
                ['Interventions', detail.interventions_provided],
                ['Resources provided', detail.resources_provided],
              ].map(([label, val]) =>
                val ? (
                  <div key={label as string}>
                    <div className="text-xs font-medium text-gray-500 uppercase">{label}</div>
                    <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{val as string}</div>
                  </div>
                ) : null
              )}
              {detail.sdoh_domains_addressed && detail.sdoh_domains_addressed.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase">SDOH domains</div>
                  <div className="text-gray-800 dark:text-gray-200">
                    {detail.sdoh_domains_addressed.join(', ')}
                  </div>
                </div>
              )}
              {!detail.subjective_notes &&
                !detail.assessment_notes &&
                !detail.plan_notes &&
                !detail.chief_complaint && (
                  <div className="text-gray-500">No documentation captured yet for this encounter.</div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
